import { useMemo, useState } from 'react';
import { StyleSheet, View } from 'react-native';
import { Menu, Text, TouchableRipple } from 'react-native-paper';

import { CheckIcon, DownIcon } from '@/assets';
import { ExtendedMenu } from '@/shared/components/ExtendedMenu';
import { useAppTheme } from '@/styles/hooks/useAppTheme';
import type { AppTheme } from '@/styles/theme';

export type RegionFilterOption<T> = {
  value: T;
  label: string;
  buttonLabel?: string;
};

const ROW_HEIGHT = 48;
const ROW_MARGIN_TOP = 4;

export const REGION_FILTER_HEIGHT = ROW_HEIGHT + ROW_MARGIN_TOP;

export const RegionFilterDropdown = <T,>({
  options,
  value,
  onSelect
}: {
  options: RegionFilterOption<T>[];
  value: T;
  onSelect: (value: T) => void;
}) => {
  const theme = useAppTheme();
  const styles = useMemo(() => makeStyles(theme), [theme]);
  const [open, setOpen] = useState(false);
  const [btnHover, setBtnHover] = useState(false);
  const activeOption = options.find((o) => o.value === value) ?? options[0];
  const activeLabel = activeOption?.buttonLabel ?? activeOption?.label ?? '';

  return (
    <View style={styles.row}>
      <ExtendedMenu
        visible={open}
        onDismiss={() => setOpen(false)}
        backgroundColor={theme.colors.surface}
        anchor={
          <TouchableRipple
            testID="meine-region-filter-trigger"
            onPress={() => setOpen(true)}
            onHoverIn={() => setBtnHover(true)}
            onHoverOut={() => setBtnHover(false)}
            rippleColor={theme.rippleColors.onSurface}
            borderless
            style={[styles.button, btnHover && styles.buttonHover]}
          >
            <View style={styles.buttonInner}>
              <Text style={styles.buttonLabel}>{activeLabel}</Text>
              <View style={styles.chevron}>
                <DownIcon fill={theme.colors.primary} width={24} height={24} />
              </View>
            </View>
          </TouchableRipple>
        }
      >
        {options.map((o) => {
          const active = o.value === value;
          return (
            <Menu.Item
              key={String(o.value)}
              testID={`meine-region-filter-option-${String(o.value)}`}
              title={o.label}
              onPress={() => {
                onSelect(o.value);
                setOpen(false);
              }}
              trailingIcon={({ color, size }) =>
                active ? (
                  <CheckIcon fill={color} width={size} height={size} />
                ) : undefined
              }
            />
          );
        })}
      </ExtendedMenu>
    </View>
  );
};

const makeStyles = (theme: AppTheme) =>
  StyleSheet.create({
    row: {
      height: ROW_HEIGHT,
      marginTop: ROW_MARGIN_TOP,
      flexDirection: 'row',
      alignItems: 'center',
      justifyContent: 'flex-end',
      paddingHorizontal: 12
    },
    button: { borderRadius: 32 },
    buttonHover: { backgroundColor: theme.stateLayers.hover },
    buttonInner: {
      flexDirection: 'row',
      alignItems: 'center',
      paddingLeft: 12
    },
    buttonLabel: {
      fontSize: 16,
      lineHeight: 24,
      fontWeight: '500',
      letterSpacing: 0.15,
      color: theme.colors.primary
    },
    chevron: { padding: 8 }
  });
